import React, { useState, useEffect } from "react";
import { RowsPhotoAlbum } from "react-photo-album";
import "react-photo-album/rows.css";
import "./HighlightGallery.css";

const MOBILE_BREAKPOINT = 640;

function useIsMobile(breakpoint = MOBILE_BREAKPOINT) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < breakpoint;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const handleChange = (e) => setIsMobile(e.matches);
    setIsMobile(mediaQuery.matches);
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [breakpoint]);

  return isMobile;
}

/**
 * Default sample photos (5) for the HIGHLIGHT collage.
 * Each item: { src, width, height, alt }.
 */
const DEFAULT_PHOTOS = [
  { src: "/img/highlight/highlight_01.jpg", width: 800, height: 600, alt: "Yacht party highlight 1" },
  { src: "/img/highlight/highlight_02.jpg", width: 600, height: 400, alt: "Yacht party highlight 2" },
  { src: "/img/highlight/highlight_03.jpg", width: 400, height: 300, alt: "Yacht party highlight 3" },
  { src: "/img/highlight/highlight_04.jpg", width: 400, height: 300, alt: "Yacht party highlight 4" },
  { src: "/img/highlight/highlight_05.jpg", width: 400, height: 300, alt: "Yacht party highlight 5" },
];

function PhotoCell({ photo }) {
  return (
    <div
      className="highlight-gallery__mobile-photo"
      style={{ aspectRatio: `${photo.width} / ${photo.height}` }}
    >
      <img src={photo.src} alt={photo.alt ?? ""} />
    </div>
  );
}

/**
 * HighlightGallery – "HIGHLIGHT" photo collage using Rows layout.
 * On mobile (< 640px): custom 4-row layout (row1: 1, row2: 2+3, row3: 4, row4: 5).
 * On desktop: react-photo-album RowsPhotoAlbum.
 *
 * @param {Object} props
 * @param {Array<{ src: string, width: number, height: number, alt?: string }>} [props.photos] - Optional; falls back to DEFAULT_PHOTOS.
 */
export function HighlightGallery({ photos }) {
  const displayPhotos = photos && photos.length > 0 ? photos : DEFAULT_PHOTOS;
  const isMobile = useIsMobile(MOBILE_BREAKPOINT);
  const useMobileLayout = isMobile && displayPhotos.length >= 5;

  return (
    <section className="highlight-gallery" aria-labelledby="highlight-gallery-title">
      <h2 id="highlight-gallery-title" className="highlight-gallery__title">
        HIGHLIGHT
      </h2>
      {useMobileLayout ? (
        <div className="highlight-gallery__mobile-rows">
          <div className="highlight-gallery__mobile-row">
            <PhotoCell photo={displayPhotos[0]} />
          </div>
          <div className="highlight-gallery__mobile-row">
            <PhotoCell photo={displayPhotos[1]} />
            <PhotoCell photo={displayPhotos[2]} />
          </div>
          <div className="highlight-gallery__mobile-row">
            <PhotoCell photo={displayPhotos[3]} />
          </div>
          <div className="highlight-gallery__mobile-row">
            <PhotoCell photo={displayPhotos[4]} />
          </div>
        </div>
      ) : (
        <div className="highlight-gallery__rows">
          <RowsPhotoAlbum
            photos={displayPhotos}
            breakpoints={[640, 1024]}
          />
        </div>
      )}
    </section>
  );
}
